import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, ChevronDown } from 'lucide-react';

const SortableItem = ({ id, children, columns, currentStatus, onStatusChange }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const handleStatusSelect = (newStatus) => {
    setShowDropdown(false);
    if (newStatus !== currentStatus) {
      onStatusChange(id, newStatus);
    }
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="relative group">
      {/* Drag Handle - Overlay */}
      <button 
        {...listeners} 
        className="absolute left-1 top-1/2 -translate-y-1/2 p-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-white/80 rounded"
      >
        <GripVertical className="w-3 h-3" />
      </button>
      
      {/* Card Content */}
      <div className="relative">
        {children}
        
        {/* Status Dropdown Button */}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowDropdown(!showDropdown); }}
          className="absolute top-2 right-2 p-1 bg-white/90 hover:bg-gray-100 rounded-md border border-gray-200 shadow-sm opacity-0 group-hover:opacity-100 transition-all z-10"
          title="Change status"
        >
          <ChevronDown className="w-3 h-3 text-gray-500" />
        </button>
        
        {/* Status Dropdown Menu */}
        {showDropdown && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setShowDropdown(false)} />
            <div className="absolute top-8 right-0 bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 min-w-[140px] z-30 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Move to</div>
              {columns.map((col) => (
                <button
                  key={col.id}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleStatusSelect(col.id); }}
                  className={`w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                    currentStatus === col.id ? 'bg-[#014DB7]/5 text-[#014DB7] font-medium' : 'text-gray-700'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${getColorClass(col.color)}`} />
                  {col.title}
                  {currentStatus === col.id && (
                    <span className="ml-auto text-[#014DB7]">✓</span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const getColorClass = (color) => {
  const colors = {
    blue: 'bg-[#014DB7]',
    green: 'bg-[#84CC16]',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    purple: 'bg-purple-500',
    gray: 'bg-gray-400',
    orange: 'bg-orange-500',
  };
  return colors[color] || colors.gray;
};

const getBgColorClass = (color) => {
  const colors = {
    blue: 'bg-[#014DB7]/5',
    green: 'bg-[#84CC16]/5',
    yellow: 'bg-yellow-50',
    red: 'bg-red-50',
    purple: 'bg-purple-50',
    gray: 'bg-gray-100',
    orange: 'bg-orange-50',
  };
  return colors[color] || colors.gray;
};

const DroppableColumn = ({ id, title, items, color, renderItem, columns, onStatusChange, isOver }) => {
  const { setNodeRef, isOver: columnIsOver } = useDroppable({ id });
  const isActive = isOver || columnIsOver;

  return (
    <div 
      ref={setNodeRef}
      style={{ width: '220px', minWidth: '220px', maxWidth: '220px' }}
      className={`flex-shrink-0 flex-grow-0 rounded-2xl transition-all duration-200 ${
        isActive 
          ? `ring-2 ring-[#014DB7] ring-offset-2 ${getBgColorClass(color)}` 
          : 'bg-gray-50/80'
      }`}
    >
      {/* Column Header */}
      <div className={`px-3 py-2.5 rounded-t-2xl ${getBgColorClass(color)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${getColorClass(color)} shadow-sm`} />
            <h3 className="font-semibold text-[#0A1628] text-xs">{title}</h3>
          </div>
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
            items.length > 0 ? `${getColorClass(color)} text-white` : 'bg-gray-200 text-gray-600'
          }`}>
            {items.length}
          </span>
        </div>
      </div>

      {/* Cards Container */}
      <div className="p-2">
        <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
          <div className={`space-y-2 min-h-[400px] rounded-xl p-1 transition-colors ${
            isActive ? 'bg-[#014DB7]/5 border-2 border-dashed border-[#014DB7]/30' : ''
          }`}>
            {items.length === 0 && (
              <div className={`flex items-center justify-center h-[80px] text-gray-400 text-xs rounded-lg border-2 border-dashed ${
                isActive ? 'border-[#014DB7]/50 bg-[#014DB7]/5' : 'border-gray-200'
              }`}>
                {isActive ? 'Drop here' : 'Empty'}
              </div>
            )}
            {items.map((item) => (
              <SortableItem 
                key={item.id} 
                id={item.id}
                columns={columns}
                currentStatus={item.status}
                onStatusChange={onStatusChange}
              >
                {renderItem(item)}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
      </div>
    </div>
  );
};

export const KanbanBoard = ({ 
  columns, 
  items, 
  onItemMove, 
  renderItem,
}) => {
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const findColumn = (id) => {
    // Check if id is a column id
    if (columns.find(col => col.id === id)) {
      return id;
    }
    // Find item and return its status
    const item = items.find(item => item.id === id);
    return item?.status;
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    setOverId(over?.id || null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) return;

    const activeColumn = findColumn(active.id);
    const overColumn = findColumn(over.id);

    if (activeColumn !== overColumn && overColumn) {
      onItemMove(active.id, overColumn);
    }
  };

  const handleStatusChange = (itemId, newStatus) => {
    onItemMove(itemId, newStatus);
  };

  const activeItem = items.find(item => item.id === activeId);
  const overColumn = overId ? findColumn(overId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-3 overflow-x-auto pb-4">
        {columns.map((column) => (
          <DroppableColumn
            key={column.id}
            id={column.id}
            title={column.title}
            color={column.color}
            items={items.filter(item => item.status === column.id)}
            renderItem={renderItem}
            columns={columns}
            onStatusChange={handleStatusChange}
            isOver={overColumn === column.id}
          />
        ))}
      </div>

      <DragOverlay>
        {activeItem ? (
          <div className="opacity-90 transform rotate-2 scale-105 shadow-2xl rounded-xl" style={{ width: '200px' }}>
            {renderItem(activeItem)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default KanbanBoard;
